import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

const UserContext = createContext({
  currentUser: null,
  loading: true,
  loginWithSupabase: async () => { },
  logout: () => { },
  setCurrentUser: () => { },
});



export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const raw = localStorage.getItem("currentUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    try {
      if (currentUser) localStorage.setItem("currentUser", JSON.stringify(currentUser));
      else localStorage.removeItem("currentUser");
    } catch { }
  }, [currentUser]);

  const logout = useCallback(async () => {
    try {
      const raw = localStorage.getItem("currentUser");
      if (raw) {
        const u = JSON.parse(raw);
        if (u && u.id) localStorage.removeItem(`taskListFilter_${u.id}`);
      }
    } catch (e) {}

    // Clear local state immediately so UI updates
    setCurrentUser(null);
    localStorage.removeItem("currentUser");

    try {
      const { error } = await supabase.auth.signOut();
      if (error) console.error("UserContext: Supabase signOut returned error:", error);
    } catch (err) {
      console.error("UserContext: Supabase signOut exception:", err);
    }
  }, []);

  const refreshUser = useCallback(async (authUser = null) => {
    try {
      let user = authUser;
      if (!user) {
        const { data } = await supabase.auth.getUser();
        user = data?.user;
      }
      if (!user) return;

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (!error && profile) {
        // Check if user account is still active
        if (profile.is_active === false) {
          // User was deactivated mid-session, log them out
          console.warn("User account deactivated, logging out...");
          await logout();
          return;
        }

        const { data: memberships } = await supabase
          .from("user_team_memberships")
          .select("department_id, team_id, role, is_department_admin")
          .eq("user_id", user.id);

        const allowedTeamIds = [
          profile.team_id,
          ...(memberships || []).map(m => m.team_id)
        ].filter(Boolean);

        const adminDeptIds = [
          profile.role === "Department Admin" ? profile.department_id : null,
          ...(memberships || [])
            .filter(m => m.is_department_admin || m.role === "Department Admin")
            .map(m => m.department_id)
        ].filter(Boolean);

        const userObj = {
          id: profile.id,
          name: profile.name,
          email: profile.email,
          role: profile.role || "User",
          team_id: profile.team_id || null,
          sub_team_ids: profile.sub_team_ids || [],
          photo_url: profile.photo_url || null,
          department_id: profile.department_id || null,
          is_active: profile.is_active !== false,
          can_manage_users: profile.can_manage_users || false,
          can_manage_teams: profile.can_manage_teams || false,
          memberships: memberships || [],
          allowedTeamIds: [...new Set(allowedTeamIds)],
          adminDeptIds: [...new Set(adminDeptIds)]
        };
        setCurrentUser(userObj);
      } else if (error) {
        console.error("Profile fetch error:", error);
      }
    } catch (err) {
      console.error("Failed to refresh user:", err);
    }
  }, [logout]);
  useEffect(() => {
    let mounted = true;
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          await refreshUser(session.user);
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    
    initAuth();
    return () => { mounted = false; };
  }, [refreshUser]);


  useEffect(() => {
    if (!supabase || typeof supabase.auth === "undefined") return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        // Non-blocking refresh
        refreshUser(session.user).catch(e => console.error("onAuthStateChange refresh failed:", e));
      } else if (event === "SIGNED_OUT" || event === "USER_DELETED") {
        setCurrentUser(null);
        localStorage.removeItem("currentUser");
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [refreshUser]);


  // loginWithSupabase remains as async function since it's not used in useEffect
  async function loginWithSupabase(email, password) {

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", authData.user.id)
        .single();

      if (profileError) {
        if (profileError.code === "PGRST116") {
          throw new Error("Your user account exists, but your profile record is missing. Please contact an admin or run the Profile Sync SQL.");
        }
        throw profileError;
      }

      // Check if user account is active
      if (profile.is_active === false) {
        // Sign out immediately
        await supabase.auth.signOut();
        throw new Error("Your account is deactivated. Please contact an administrator.");
      }

      const { data: memberships } = await supabase
        .from("user_team_memberships")
        .select("department_id, team_id, role, is_department_admin")
        .eq("user_id", authData.user.id);

      const allowedTeamIds = [
        profile.team_id,
        ...(memberships || []).map(m => m.team_id)
      ].filter(Boolean);

      const adminDeptIds = [
        profile.role === "Department Admin" ? profile.department_id : null,
        ...(memberships || [])
          .filter(m => m.is_department_admin || m.role === "Department Admin")
          .map(m => m.department_id)
      ].filter(Boolean);

      const userObj = {
        id: profile.id,
        name: profile.name,
        email: profile.email,
        role: profile.role || "User",
        team_id: profile.team_id || null,
        sub_team_ids: profile.sub_team_ids || [],
        photo_url: profile.photo_url || null,
        department_id: profile.department_id || null,
        is_active: profile.is_active !== false,
        can_manage_users: profile.can_manage_users || false,
        can_manage_teams: profile.can_manage_teams || false,
        memberships: memberships || [],
        allowedTeamIds: [...new Set(allowedTeamIds)],
        adminDeptIds: [...new Set(adminDeptIds)]
      };

      setCurrentUser(userObj);
      return userObj;
    } catch (err) {
      throw err;
    }
  }

  return (
    <UserContext.Provider value={{ currentUser, loading, loginWithSupabase, logout, refreshUser, setCurrentUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
